import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import { getMyPurchases } from "../services/purchase";
import Sidebar from "../components/Sidebar";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Zap, Trophy, Award, Medal, Star, Target,
  Gamepad2, Puzzle, Brain, Coins, Flame, Compass,
  ChevronRight, Library, Play, BadgeCheck, BookOpen, CalendarClock,
  Mic, Users, Bell, PlayCircle, MailOpen, ArrowRight, Check, Trash2
} from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';

function CircularProgress({ pct, size = 90, stroke = 8, color = "#9333EA" }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90 filter drop-shadow-md flex-shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke} className="opacity-30" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color}
        strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={off}
        strokeLinecap="round" className="transition-all duration-1000 ease-out" />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        className="fill-slate-900 dark:fill-white text-base font-black"
        transform={`rotate(90 ${size / 2} ${size / 2})`}>{Math.round(pct)}%</text>
    </svg>
  );
}

export default function KidsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get("tab");

  const [user, setUser] = useState(null);
  const [purchasedCourses, setPurchasedCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameStats, setGameStats] = useState({ stars: 0, xp: 0, level: 1, streak: 1 });
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({ overall_progress: 0, courses_enrolled: 0, pending_assignments: 0, rank: "N/A", total_students: 0, certificates_earned: 0 });
  const [selectedAvatar, setSelectedAvatar] = useState(0);
  const [allExams, setAllExams] = useState([]);

  // 🔔 Notifications State
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Calculate custom views based on sidebar active parameter
  const getFilteredView = () => {
    if (activeTab === "my-courses") {
      return {
        title: "My Purchased Courses",
        subtitle: "View and open all your owned materials",
        courses: purchasedCourses,
        emptyMessage: "You haven't purchased any courses yet!"
      };
    }
    if (activeTab === "continue") {
      const inProgress = purchasedCourses.filter(c => (c.progress || 0) > 0);
      return {
        title: "Continue Learning",
        subtitle: "Resume your active studies exactly where you left off",
        courses: inProgress,
        emptyMessage: "You haven't started any courses yet. Go to 'My Courses' to begin your first lesson!"
      };
    }
    return null;
  };
  const filteredView = getFilteredView();


  useEffect(() => {
    if (activeTab === "notifications") {
      fetchNotifications();
    }
  }, [activeTab]);

  const fetchNotifications = async () => {
    setNotificationsLoading(true);
    try {
      const res = await api.get("/notifications/");
      setNotifications(res.data || []);
    } catch (err) {
      console.error("Failed to load notifications");
    } finally {
      setNotificationsLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) {
      console.error("Failed to mark notification read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post("/notifications/mark-read");
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      toast.success("All notifications cleared!");
    } catch (err) {
      toast.error("Unable to clear all.");
    }
  };

  const init = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) return;
      const decoded = jwtDecode(token);
      setUser(decoded);

      const [purchaseRes, coursesRes, booksRes, statsRes, gameRes, badgesRes, examsRes, userRes] = await Promise.all([
        getMyPurchases().catch(() => ({ data: [] })),
        api.get("/courses").catch(() => ({ data: [] })),
        api.get("/books").catch(() => ({ data: [] })),
        api.get("/student/stats").catch(() => ({ data: {} })),
        api.get("/gamification/stats").catch(() => ({ data: { stars: 0, xp: 0, level: 1, streak: 1 } })),
        api.get("/gamification/badges").catch(() => ({ data: [] })),
        api.get("/exams/all").catch(() => ({ data: [] })),
        api.get("/users/me").catch(() => ({ data: {} }))
      ]);

      setPurchasedCourses(purchaseRes.data || []);
      setAllCourses(coursesRes.data || []);
      setBooks(booksRes.data || []);
      setStats(statsRes.data || { overall_progress: 0, courses_enrolled: 0, pending_assignments: 0, rank: "N/A", total_students: 0, certificates_earned: 0 });
      setGameStats(gameRes.data || { stars: 0, xp: 0, level: 1, streak: 1 });
      setBadges(badgesRes.data || []);
      setAllExams(examsRes.data || []);

      // Set persistent selected avatar index from user profile
      if (userRes.data?.profile_picture) {
        const idx = parseInt(userRes.data.profile_picture, 10);
        if (!isNaN(idx) && idx >= 0 && idx < 4) {
          setSelectedAvatar(idx);
        }
      }

    } catch (err) {
      console.error("Error loading kid metrics:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleUpdateAvatar = async (idx) => {
    setSelectedAvatar(idx);
    try {
      await api.put("/users/me", { profile_picture: idx.toString() });
      toast.success("Learning buddy updated! ✨");
    } catch (err) {
      console.error("Failed to save selected avatar.");
    }
  };


  useEffect(() => {
    init();
  }, [init]);

  if (loading || !user) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#FFFAEE]">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
          className="w-16 h-16 border-8 border-yellow-400 border-t-purple-600 rounded-full"
        />
      </div>
    );
  }

  const userName = user.full_name || user.email.split('@')[0];
  const xpProgress = (gameStats.xp || 0) % 100;
  const coins = Math.floor((gameStats.xp || 0) / 10) + (gameStats.stars || 0) * 5;


  const avatarList = [
    { name: "Ace Captain", color: "bg-indigo-500", face: "bg-indigo-50", shadow: "shadow-indigo-200", accent: "🚀" },
    { name: "Fire Fox", color: "bg-orange-500", face: "bg-orange-50", shadow: "shadow-orange-200", accent: "🦊" },
    { name: "Green Dino", color: "bg-emerald-500", face: "bg-emerald-50", shadow: "shadow-emerald-200", accent: "🦖" },
    { name: "Panda Master", color: "bg-slate-700", face: "bg-slate-50", shadow: "shadow-slate-200", accent: "🐼" },
  ];

  return (
    <div className="min-h-screen bg-[#F3F0FF] dark:bg-slate-950 flex font-['Inter',sans-serif] relative overflow-hidden">
      <Toaster position="top-center" />

      {/* Playful background shapes */}
      <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-yellow-200/40 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute bottom-[10%] left-[20%] w-72 h-72 bg-pink-200/40 rounded-full blur-3xl pointer-events-none" />

      <Sidebar role="student" />

      <main className="flex-1 lg:ml-64 p-6 lg:p-10 space-y-10 pb-24 z-10 relative">

        {/* ─── NOTIFICATIONS TAB ─── */}
        {activeTab === "notifications" ? (
          <section className="space-y-8 animate-slide-up">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                  <Bell size={32} className="text-purple-600" /> Notifications Hub
                </h1>
                <p className="text-slate-500 mt-2 font-semibold text-lg">Stay updated with lessons, deadlines, and feedback</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={markAllAsRead}
                  disabled={notifications.every(n => n.is_read)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-purple-50 text-purple-600 dark:bg-slate-800 dark:text-purple-400 font-black rounded-xl hover:bg-purple-100 text-sm transition-all shadow-sm disabled:opacity-50"
                >
                  <MailOpen size={16} /> Clear All
                </button>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="px-5 py-2.5 bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 font-black rounded-xl hover:bg-slate-200 text-sm transition-all shadow-sm"
                >
                  ← Back
                </button>
              </div>
            </div>

            {notificationsLoading ? (
              <div className="py-20 flex justify-center"><div className="w-10 h-10 border-4 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" /></div>
            ) : notifications.length === 0 ? (
              <div className="py-20 flex flex-col items-center justify-center text-center bg-white dark:bg-slate-900/50 rounded-[2.5rem] border-4 border-dashed border-slate-200 dark:border-slate-800">
                <Bell size={64} className="text-purple-200 dark:text-purple-950 mb-4 opacity-30" />
                <p className="text-slate-500 dark:text-slate-400 text-xl font-black">You're all caught up! 🎉</p>
                <p className="text-slate-400 text-sm mt-2">No new notifications at the moment.</p>
              </div>
            ) : (
              <div className="space-y-4 max-w-4xl">
                {notifications.map(n => (
                  <motion.div
                    key={n.id}
                    whileHover={{ x: 4 }}
                    onClick={() => !n.is_read && markAsRead(n.id)}
                    className={`relative p-6 rounded-3xl border cursor-pointer transition-all flex items-start gap-5 ${n.is_read ? 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 opacity-70 hover:opacity-100' : 'bg-gradient-to-r from-purple-50/50 to-white dark:from-purple-950/20 dark:to-slate-900 border-purple-100 dark:border-purple-900 shadow-md'}`}
                  >
                    {!n.is_read && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-purple-600 rounded-r-full" />
                    )}
                    <div className={`p-3 rounded-2xl ${n.type === 'assignment' ? 'bg-orange-50 text-orange-500 dark:bg-orange-950/20' : n.type === 'exam' ? 'bg-rose-50 text-rose-500 dark:bg-rose-950/20' : 'bg-blue-50 text-blue-500 dark:bg-blue-950/20'}`}>
                      {n.type === 'assignment' ? <BookOpen size={20} /> : n.type === 'exam' ? <Award size={20} /> : <Bell size={20} />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className={`font-black text-lg leading-tight ${n.is_read ? 'text-slate-700 dark:text-slate-300' : 'text-slate-900 dark:text-white'}`}>{n.title}</h4>
                        <span className="text-[10px] font-bold text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-2 leading-relaxed">{n.message}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        ) : filteredView ? (

          /* ─── DYNAMIC SIDEBAR TABS (MY COURSES / CONTINUE LEARNING) ─── */
          <section className="space-y-8 animate-slide-up">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                  {filteredView.title}
                </h1>
                <p className="text-slate-500 mt-2 font-semibold text-lg">{filteredView.subtitle}</p>
              </div>
              <button
                onClick={() => navigate("/dashboard")}
                className="px-6 py-2.5 bg-purple-50 dark:bg-slate-800 text-purple-600 dark:text-purple-400 font-black rounded-xl hover:bg-purple-100 text-sm transition-all shadow-sm"
              >
                ← Back to Dashboard
              </button>
            </div>

            {filteredView.courses.length === 0 ? (
              <div className="py-20 flex flex-col items-center justify-center text-center bg-white dark:bg-slate-900/50 rounded-[3rem] border-4 border-dashed border-slate-200 dark:border-slate-800">
                <PlayCircle size={64} className="text-purple-200 dark:text-purple-950 mb-4" />
                <p className="text-slate-500 dark:text-slate-400 text-xl font-black max-w-md px-6 leading-relaxed">
                  {filteredView.emptyMessage}
                </p>
                {activeTab === "continue" && (
                  <button onClick={() => navigate("/dashboard?tab=my-courses")} className="mt-6 px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-black rounded-2xl shadow-xl shadow-purple-600/20 transition-all">
                    View Purchased Courses
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredView.courses.map((c) => (
                  <motion.div
                    key={c.id}
                    whileHover={{ y: -4 }}
                    className="bg-white dark:bg-slate-900 border-b-8 border-slate-200 dark:border-slate-800 rounded-[3rem] p-6 flex items-center gap-6 shadow-xl group cursor-pointer"
                    onClick={() => navigate(`/course/${c.id}`)}
                  >
                    <div className="w-24 h-24 rounded-[2rem] bg-purple-100 dark:bg-purple-950 overflow-hidden border border-slate-100 dark:border-slate-800 flex-shrink-0 relative">
                      {c.thumbnail ? (
                        <img src={c.thumbnail} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">🎒</div>
                      )}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Play className="text-white fill-white" size={24} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-lg font-black text-slate-900 dark:text-white truncate tracking-tight">{c.title}</h4>
                      <div className="mt-4 space-y-1.5">
                        <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase">
                          <span>Adventure Progress</span>
                          <span>{c.progress || 0}%</span>
                        </div>
                        <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden border border-slate-200 dark:border-slate-700">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${c.progress || 0}%` }}
                            transition={{ duration: 1.2 }}
                            className="h-full bg-emerald-400 rounded-full"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        ) : (
          <>

            {/* Top Floating Bar with Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Level", value: gameStats.level, icon: Zap, bg: "bg-yellow-400", border: "border-yellow-500", glow: "shadow-yellow-400/30" },
                { label: "Stars", value: gameStats.stars, icon: Star, bg: "bg-purple-500", border: "border-purple-600", glow: "shadow-purple-500/30" },
                { label: "XP", value: gameStats.xp, icon: Compass, bg: "bg-sky-400", border: "border-sky-500", glow: "shadow-sky-400/30" },
                { label: "Day Streak", value: `${gameStats.streak || 1} 🔥`, icon: Flame, bg: "bg-pink-500", border: "border-pink-600", glow: "shadow-pink-500/30" },
              ].map((st, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.05, y: -4 }}
                  className={`p-4 rounded-[2rem] border-b-8 ${st.bg} ${st.border} text-white shadow-xl ${st.glow} flex items-center gap-4`}
                >
                  <div className="bg-white/20 p-2.5 rounded-2xl">
                    <st.icon className="w-6 h-6" fill="currentColor" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-black text-white/80">{st.label}</p>
                    <p className="text-2xl font-black leading-none mt-1">{st.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Academic Progress Scoreboard (Mirrors User screenshot metrics dynamically) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Master Academy Ring */}
              <motion.div
                whileHover={{ y: -4 }}
                className="lg:col-span-4 bg-white dark:bg-slate-900 p-6 rounded-[2.5rem] border-b-8 border-indigo-100 dark:border-slate-800 flex items-center gap-6 shadow-xl shadow-indigo-100/20 dark:shadow-none relative overflow-hidden group"
              >
                <div className="absolute -right-4 -top-4 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl group-hover:bg-indigo-500/20 transition-all" />
                <CircularProgress pct={stats.overall_progress} color="#4F46E5" />
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Total Academy Mastery</p>
                  <p className="text-3xl font-black text-indigo-900 dark:text-indigo-400 tracking-tight leading-none">{stats.overall_progress}%</p>
                  <p className="text-[11px] font-semibold text-slate-500 mt-2 leading-tight">Complete courses to grow your mastery!</p>
                </div>
              </motion.div>

              {/* Dynamic Counts Grid */}
              <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: "Courses Enrolled", value: stats.courses_enrolled, icon: BookOpen, bg: "bg-blue-500", text: "text-blue-500", lightBg: "bg-blue-50 dark:bg-blue-950/30" },
                  { label: "Assignments Pending", value: stats.pending_assignments, icon: CalendarClock, bg: "bg-rose-500", text: "text-rose-500", lightBg: "bg-rose-50 dark:bg-rose-950/30" },
                  { label: "Certificates Earned", value: stats.certificates_earned, icon: Award, bg: "bg-emerald-500", text: "text-emerald-500", lightBg: "bg-emerald-50 dark:bg-emerald-950/30" },
                  { label: "Global Rank", value: stats.rank === "N/A" ? "..." : `#${stats.rank}`, icon: Trophy, bg: "bg-amber-500", text: "text-amber-500", lightBg: "bg-amber-50 dark:bg-amber-950/30" },
                ].map((metric, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ scale: 1.03, y: -3 }}
                    className="bg-white dark:bg-slate-900 p-5 rounded-[2rem] border-b-8 border-slate-100 dark:border-slate-800 flex flex-col justify-between shadow-md group relative"
                  >
                    <div className={`p-3 rounded-2xl ${metric.lightBg} ${metric.text} w-fit mb-4 group-hover:scale-110 transition-transform`}>
                      <metric.icon size={20} />
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-tight mb-1">{metric.label}</p>
                      <p className={`text-2xl font-black tracking-tight ${metric.text} dark:text-white leading-none`}>{metric.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Dynamic Welcome Section */}
            <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="lg:col-span-2 bg-gradient-to-br from-[#FF8552] to-[#FF6B35] rounded-[3rem] p-8 md:p-10 text-white border-b-8 border-orange-700 shadow-2xl relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-[url('https://transparenttextures.com/patterns/skulls-and-crossbones.png')] opacity-10 group-hover:opacity-20 transition-opacity" />
                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="space-y-4 text-center md:text-left">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-xs font-black tracking-widest uppercase border border-white/30 animate-bounce">
                      <Sparkles size={14} /> Adventure Mode Active
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black tracking-tight drop-shadow-md">
                      Hey, {userName}! 🎮
                    </h1>
                    <p className="text-orange-50 text-base md:text-lg font-bold max-w-md opacity-90 leading-relaxed">
                      You're crushing it! Complete challenges today to level up your avatar buddy. Ready?
                    </p>
                    <div className="pt-4 flex flex-wrap gap-3 justify-center md:justify-start">
                      <button
                        onClick={() => navigate("/exams")}
                        className="px-8 py-4 bg-yellow-300 hover:bg-yellow-400 text-orange-900 font-black rounded-[1.5rem] border-b-4 border-yellow-500 text-base transition-all shadow-lg active:translate-y-1 active:border-b-0"
                      >
                        Play & Learn 🚀
                      </button>
                    </div>
                  </div>
                  <motion.div
                    animate={{ y: [0, -15, 0] }}
                    transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
                    className="w-36 h-36 bg-white/20 backdrop-blur-md border border-white/40 rounded-[2.5rem] flex items-center justify-center shadow-2xl text-6xl"
                  >
                    {avatarList[selectedAvatar].accent}
                  </motion.div>
                </div>
              </motion.div>

              {/* Kid XP Progress Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-white dark:bg-slate-900 rounded-[3rem] p-8 border-b-8 border-slate-200 dark:border-slate-800 shadow-xl flex flex-col justify-between group"
              >
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">XP Goal 🎯</h3>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-black">Level {gameStats.level}</span>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-sm font-semibold leading-relaxed">
                    Collect {100 - xpProgress} more XP to reach Level {gameStats.level + 1}!
                  </p>
                </div>

                <div className="my-6 relative">
                  <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase mb-2">
                    <span>Start</span>
                    <span>Next Level</span>
                  </div>
                  <div className="w-full h-8 bg-slate-100 dark:bg-slate-800 rounded-full border-2 border-slate-200 dark:border-slate-700 overflow-hidden p-1">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${xpProgress}%` }}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full relative"
                    >
                      <div className="absolute top-0 right-0 bottom-0 left-0 bg-[linear-gradient(45deg,rgba(255,255,255,.15)_25%,transparent_25%,transparent_50%,rgba(255,255,255,.15)_50%,rgba(255,255,255,.15)_75%,transparent_75%,transparent)] bg-[length:20px_20px] animate-[stripe_1s_linear_infinite]" />
                    </motion.div>
                  </div>
                  <div className="absolute right-0 -bottom-6 bg-yellow-400 text-xs text-yellow-900 font-black px-2 py-0.5 rounded-lg border border-yellow-500 animate-pulse shadow">
                    {xpProgress}% Complete
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-indigo-50 dark:bg-slate-800/50 p-4 rounded-2xl mt-4">
                  <Coins className="text-indigo-500 w-6 h-6" />
                  <div>
                    <p className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Redeemable Coins</p>
                    <p className="text-lg font-black text-slate-900 dark:text-white leading-none">{coins} Coins</p>
                  </div>
                </div>
              </motion.div>
            </section>

            {/* Badges & Trophies */}
            <section className="space-y-6">
              <div className="flex justify-between items-end">
                <div>
                  <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3 tracking-tight">
                    <Medal className="text-[#9333EA]" size={28} /> My Cool Badges
                  </h2>
                  <p className="text-slate-500 font-semibold text-sm mt-1">Check out the awesome trophies you've collected!</p>
                </div>
                <span className="px-4 py-2 bg-[#9333EA]/10 text-[#9333EA] text-xs font-black rounded-full uppercase tracking-widest">
                  {badges.length} Unlocked
                </span>
              </div>

              {badges.length === 0 ? (
                <div className="bg-white dark:bg-slate-900 border-4 border-dashed border-slate-200 dark:border-slate-800 rounded-[3rem] p-12 text-center shadow-inner">
                  <Award size={60} className="mx-auto text-slate-300 animate-bounce mb-4" />
                  <h4 className="text-lg font-black text-slate-700 dark:text-slate-300">Start Earning Badges! 🏆</h4>
                  <p className="text-sm text-slate-400 mt-2 max-w-xs mx-auto font-medium">Finish active courses or play quizzes to unlock super badges here!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                  {badges.map((badge, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ scale: 1.1, rotate: 5, y: -5 }}
                      className="bg-white dark:bg-slate-900 rounded-[2.5rem] p-5 border-b-4 border-slate-200 dark:border-slate-800 text-center relative group shadow-lg"
                    >
                      <div className="relative w-20 h-20 mx-auto bg-gradient-to-tr from-yellow-100 to-orange-50 rounded-full flex items-center justify-center shadow-md">
                        <div className="text-4xl transition-transform group-hover:scale-125 duration-300">{badge.badge_icon || "🏆"}</div>
                        <div className="absolute inset-0 rounded-full bg-yellow-400 opacity-0 group-hover:opacity-10 blur-md transition-opacity" />
                      </div>
                      <h5 className="mt-4 text-sm font-black text-slate-900 dark:text-white truncate tracking-tight">{badge.badge_name}</h5>
                      <p className="text-[9px] text-slate-400 font-bold mt-1 uppercase tracking-wider">{new Date(badge.awarded_at).toLocaleDateString()}</p>
                    </motion.div>
                  ))}
                </div>
              )}
            </section>

            {/* ⚡ High Octane Gamification Hub Tiles */}
            <section className="space-y-6">
              <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3 tracking-tight">
                <Zap className="text-[#FF8552]" size={28} fill="currentColor" /> Gamified Missions Hub
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { label: "Speak to Voice AI", desc: "Speak directly to your learning tutor!", icon: Mic, color: "from-[#818CF8] to-[#6366F1]", border: "border-[#4F46E5]", route: "/voice-tutor", sticker: "🗣️" },
                  { label: "Daily Quests", desc: "Answer daily trivia to win points!", icon: Flame, color: "from-[#FBBF24] to-[#F59E0B]", border: "border-[#D97706]", route: "/daily-challenges", sticker: "🔥" },
                  { label: "Classroom Rivals", desc: "Compete with friends for Rank #1", icon: Users, color: "from-[#F472B6] to-[#EC4899]", border: "border-[#DB2777]", route: "/friend-competition", sticker: "⚔️" },
                  { label: "Smart Revision", desc: "AI memory hacks for hard topics", icon: Brain, color: "from-[#38BDF8] to-[#0EA5E9]", border: "border-[#0284C7]", route: "/ai-revision", sticker: "⚡" },
                ].map((tile, tidx) => (
                  <motion.button
                    key={tidx}
                    whileHover={{ scale: 1.04, y: -6 }}
                    onClick={() => navigate(tile.route)}
                    className={`relative overflow-hidden rounded-[2.5rem] text-left bg-gradient-to-br ${tile.color} p-6 text-white shadow-2xl border-b-[10px] ${tile.border} active:translate-y-1 active:border-b-2 transition-all group`}
                  >
                    <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full blur-xl group-hover:scale-125 transition-transform" />

                    <div className="flex justify-between items-start mb-4">
                      <div className="bg-white/20 w-12 h-12 rounded-2xl flex items-center justify-center border border-white/30 shadow-md">
                        <tile.icon size={24} className="text-white" />
                      </div>
                      <span className="text-2xl filter drop-shadow-sm">{tile.sticker}</span>
                    </div>

                    <h4 className="text-lg font-black tracking-tight leading-snug mb-1">{tile.label}</h4>
                    <p className="text-white/80 text-[11px] font-bold leading-snug min-h-[2rem]">{tile.desc}</p>

                    <div className="mt-4 pt-3 border-t border-white/20 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-yellow-200 group-hover:translate-x-1 transition-transform">
                      Enter Arena <ChevronRight size={12} />
                    </div>
                  </motion.button>
                ))}
              </div>
            </section>

            {/* Big Action Tiles for Gamified Modules */}
            <section className="space-y-6">
              <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3 tracking-tight">
                <Gamepad2 className="text-emerald-500" size={28} /> Adventure Quizzes
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {allExams.length === 0 ? (
                  <div className="md:col-span-3 p-12 text-center bg-white dark:bg-slate-900 rounded-[3rem] border-4 border-dashed border-slate-200 dark:border-slate-800 shadow-inner">
                    <Gamepad2 size={64} className="text-slate-300 mx-auto mb-4 animate-bounce" />
                    <h4 className="text-lg font-black text-slate-700 dark:text-slate-300">New Quizzes Loading Soon! 🎮</h4>
                    <p className="text-sm text-slate-400 mt-2 font-semibold">Ask your teacher to publish fun online exams for your quest!</p>
                  </div>
                ) : (
                  allExams.slice(0, 3).map((exam, i) => {
                    const colors = ["bg-gradient-to-br from-[#38BDF8] to-[#0EA5E9]", "bg-gradient-to-br from-[#4ADE80] to-[#16A34A]", "bg-gradient-to-br from-[#C084FC] to-[#9333EA]"];
                    const icons = [Puzzle, Brain, Sparkles];
                    const textColors = ["text-sky-50", "text-emerald-50", "text-purple-50"];

                    const colorClass = colors[i % colors.length];
                    const Icon = icons[i % icons.length];
                    const textColorClass = textColors[i % textColors.length];
                    const starCount = (i % 3) + 3;

                    return (
                      <motion.button
                        key={exam.id}
                        whileHover={{ scale: 1.03, y: -8 }}
                        onClick={() => navigate("/exams")}
                        className={`group relative overflow-hidden rounded-[3rem] text-left ${colorClass} p-6 text-white shadow-2xl shadow-slate-200 dark:shadow-none border-b-[12px] border-black/20 active:translate-y-1 active:border-b-4 transition-all`}
                      >
                        <div className="absolute top-[-20%] right-[-10%] w-32 h-32 bg-white/20 rounded-full blur-xl group-hover:scale-125 transition-transform duration-500" />

                        <div className="bg-white/20 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 border border-white/30">
                          <Icon size={28} className="text-white" />
                        </div>

                        <h4 className="text-2xl font-black tracking-tight leading-tight line-clamp-1">{exam.title}</h4>
                        <p className={`${textColorClass} text-sm mt-2 font-semibold opacity-90 min-h-[2.5rem]`}>
                          Test your knowledge in {exam.category || "General Topic"}! {exam.duration_minutes} mins.
                        </p>

                        <div className="mt-6 flex items-center justify-between bg-white/20 rounded-2xl px-4 py-3 border border-white/20">
                          <div className="flex items-center gap-1 text-yellow-200">
                            {Array.from({ length: starCount }).map((_, sIdx) => (
                              <Star key={sIdx} size={14} fill="currentColor" className="animate-pulse" />
                            ))}
                          </div>
                          <span className="text-[10px] font-black tracking-widest uppercase bg-white text-slate-900 px-2.5 py-1 rounded-lg shadow-sm">
                            {exam.category || "QUEST"} 🎖️
                          </span>
                        </div>
                      </motion.button>
                    );
                  })
                )}
              </div>
            </section>

            {/* Customize Avatar Section */}
            <section className="bg-white dark:bg-slate-900 rounded-[3rem] border-b-8 border-slate-200 dark:border-slate-800 p-8 shadow-xl space-y-6">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                <div>
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3 tracking-tight">
                    🎭 Learning Buddy
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 font-semibold text-sm mt-1">Pick your active avatar to guide your journey.</p>
                </div>
                <span className="text-xs font-black uppercase text-indigo-600 tracking-widest bg-indigo-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg">Unlocked by Leveling Up</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                {avatarList.map((av, i) => (
                  <motion.button
                    key={i}
                    whileHover={{ scale: 1.05, y: -4 }}
                    onClick={() => handleUpdateAvatar(i)}
                    className={`relative p-6 rounded-[2.5rem] border-4 text-center transition-all ${selectedAvatar === i ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/20' : 'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900'}`}
                  >
                    <div className={`mx-auto w-20 h-20 rounded-[2rem] ${av.color} flex items-center justify-center text-4xl shadow-xl ${av.shadow}`}>
                      {av.accent}
                    </div>
                    <h5 className="mt-4 font-black text-slate-900 dark:text-white tracking-tight">{av.name}</h5>
                    <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider mt-1 block">
                      {selectedAvatar === i ? "⭐ Active" : "Pick Me"}
                    </span>
                  </motion.button>
                ))}
              </div>
            </section>

            {/* Student Dynamic Continue Learning Section */}
            {purchasedCourses.length > 0 && (
              <section className="space-y-6">
                <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3 tracking-tight">
                  <Library className="text-sky-500" size={28} /> My Active Journeys
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {purchasedCourses.slice(0, 2).map((c, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ y: -4 }}
                      className="bg-white dark:bg-slate-900 border-b-8 border-slate-200 dark:border-slate-800 rounded-[3rem] p-6 flex items-center gap-6 shadow-xl group cursor-pointer"
                      onClick={() => navigate(`/course/${c.id}`)}
                    >
                      <div className="w-24 h-24 rounded-[2rem] bg-indigo-100 dark:bg-indigo-950 overflow-hidden border border-slate-100 dark:border-slate-800 flex-shrink-0 relative">
                        {c.thumbnail ? (
                          <img src={c.thumbnail} className="w-full h-full object-cover" alt="" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-2xl">🎒</div>
                        )}
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <Play className="text-white fill-white" size={24} />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-black text-slate-900 dark:text-white truncate tracking-tight">{c.title}</h4>
                        <div className="mt-4 space-y-1.5">
                          <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase">
                            <span>Adventure Progress</span>
                            <span>{c.progress || 0}%</span>
                          </div>
                          <div className="w-full h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden border border-slate-200 dark:border-slate-700">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${c.progress || 0}%` }}
                              transition={{ duration: 1.2 }}
                              className="h-full bg-emerald-400 rounded-full"
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            )}

          </>
        )}

      </main>
    </div>
  );
}
